<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import {
  fetchMarketplaceJson,
  postMarketplaceJson,
} from 'dashboard/helpers/marketplaceApi';
import { formatCurrency } from '../helpers/orderHelpers';
import { useAdsSpeech } from '../composables/useAdsSpeech';

const route = useRoute();
const router = useRouter();

const code = computed(() => route.params.code);
const isLoading = ref(false);
const loadError = ref('');
const profile = ref(null);
const analysis = ref(null);

const isAnalyzing = ref(false);
const analysisError = ref('');

const { isSpeaking, isLoadingAudio, toggleSpeaking, stopSpeaking } =
  useAdsSpeech();

const load = async () => {
  isLoading.value = true;
  loadError.value = '';
  stopSpeaking();
  try {
    const data = await fetchMarketplaceJson(
      route.params.accountId,
      `meta_ads/product?code=${encodeURIComponent(code.value)}`
    );
    profile.value = data.profile;
    analysis.value = data.analysis;
  } catch {
    loadError.value = 'Ürün verisi yüklenemedi.';
  } finally {
    isLoading.value = false;
  }
};

const runAnalysis = async () => {
  isAnalyzing.value = true;
  analysisError.value = '';
  stopSpeaking();
  try {
    const data = await postMarketplaceJson(
      route.params.accountId,
      `meta_ads/product_analyze?code=${encodeURIComponent(code.value)}`
    );
    analysis.value = data.analysis;
  } catch {
    analysisError.value = 'Analiz alınamadı, tekrar deneyin.';
  } finally {
    isAnalyzing.value = false;
  }
};

const buildSpeechText = () => {
  if (!analysis.value) return '';
  const a = analysis.value;
  return [
    `En büyük fırsat. ${a.opportunity}`,
    `En büyük risk. ${a.risk}`,
    `Bugün yapılacak. ${a.action}`,
    a.narrative,
  ]
    .filter(Boolean)
    .join('. ');
};

const num = n => Number(n || 0).toLocaleString('tr-TR');
const fmtDate = iso => {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}.${m}.${y}`;
};
const dateRange = period => {
  if (!period?.ilk) return period?.aciklama || '';
  return `${fmtDate(period.ilk)} – ${fmtDate(period.son)}`;
};
const arrow = dir => (dir === 'yukari' ? '▲' : dir === 'asagi' ? '▼' : '▬');
const arrowClass = dir =>
  dir === 'yukari'
    ? 'text-n-teal-11'
    : dir === 'asagi'
      ? 'text-n-ruby-11'
      : 'text-n-slate-11';

const confidenceClass = c =>
  c === 'yüksek'
    ? 'bg-n-teal-9/15 text-n-teal-11'
    : c === 'düşük'
      ? 'bg-n-ruby-9/15 text-n-ruby-11'
      : 'bg-n-amber-9/15 text-n-amber-11';

// Kampanyalari reklam hesabina gore grupla (kac hesap olursa olsun hepsi gorunsun)
const campaignsByAccount = computed(() => {
  const groups = {};
  (profile.value?.kampanyalar || []).forEach(c => {
    const key = c.hesap || '—';
    (groups[key] = groups[key] || []).push(c);
  });
  return Object.entries(groups);
});

const goBack = () => router.push({ name: 'product_intel_index' });

onMounted(load);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <header
      class="flex items-center gap-3 px-5 py-4 border-b sm:px-7 border-n-weak flex-shrink-0"
    >
      <button
        type="button"
        class="flex items-center justify-center rounded-lg size-9 text-n-slate-12 hover:bg-n-alpha-1"
        @click="goBack"
      >
        <span class="size-5 i-lucide-arrow-left" />
      </button>
      <div class="min-w-0">
        <h1 class="text-2xl font-bold text-n-slate-12">Ürün {{ code }}</h1>
        <p class="text-base truncate text-n-slate-11">
          {{ profile?.urun?.baslik }}
        </p>
      </div>
    </header>

    <div class="flex-1 overflow-y-auto">
      <div class="px-5 py-6 mx-auto max-w-5xl sm:px-7">
        <div
          v-if="isLoading"
          class="flex flex-col items-center justify-center gap-3 py-20 text-n-slate-11"
        >
          <Spinner />
          <p class="text-base">Ürün verisi hazırlanıyor…</p>
        </div>

        <div
          v-else-if="loadError"
          class="flex flex-col items-center gap-3 py-20 text-n-slate-11"
        >
          <span class="size-10 i-lucide-alert-triangle text-n-ruby-9" />
          <p class="text-base">{{ loadError }}</p>
        </div>

        <template v-else-if="profile">
          <!-- ============ GM AI YORUMU (en üstte) ============ -->
          <section
            class="p-5 mb-6 rounded-2xl bg-n-solid-2 outline outline-1 outline-n-weak sm:p-6"
          >
            <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2
                class="flex items-center gap-2 text-xl font-bold text-n-slate-12"
              >
                <span class="size-6 i-lucide-brain text-n-brand" />
                Genel Müdür Yorumu
              </h2>
              <div class="flex gap-2">
                <button
                  v-if="analysis"
                  type="button"
                  class="flex items-center gap-2 px-4 py-2.5 text-base font-semibold rounded-lg text-white outline outline-1"
                  :class="
                    isSpeaking || isLoadingAudio
                      ? 'bg-n-ruby-9 outline-n-ruby-9'
                      : 'bg-n-teal-9 outline-n-teal-9'
                  "
                  @click="toggleSpeaking(buildSpeechText())"
                >
                  <Spinner v-if="isLoadingAudio" class="size-5" />
                  <span
                    v-else
                    :class="
                      isSpeaking ? 'i-lucide-square' : 'i-lucide-volume-2'
                    "
                    class="size-5"
                  />
                  {{
                    isLoadingAudio
                      ? 'Ses hazırlanıyor…'
                      : isSpeaking
                        ? 'Durdur'
                        : 'Sesli Dinle'
                  }}
                </button>
                <button
                  type="button"
                  class="flex items-center gap-2 px-4 py-2.5 text-base font-semibold text-white rounded-lg bg-n-brand disabled:opacity-60"
                  :disabled="isAnalyzing"
                  @click="runAnalysis"
                >
                  <Spinner v-if="isAnalyzing" class="size-5" />
                  <span v-else class="size-5 i-lucide-wand-2" />
                  {{ analysis ? 'Yeniden Analiz Et' : 'Analiz Et' }}
                </button>
              </div>
            </div>

            <p v-if="analysisError" class="text-base text-n-ruby-11">
              {{ analysisError }}
            </p>

            <div
              v-else-if="!analysis && !isAnalyzing"
              class="text-base text-n-slate-11"
            >
              Bu ürünü bir genel müdür gözüyle yorumlatmak için
              <strong>“Analiz Et”</strong>e bas. Sana en büyük fırsatı, en büyük
              riski ve bugün yapılacak tek şeyi söyler. Sesli de
              dinleyebilirsin.
            </div>

            <div v-else-if="isAnalyzing" class="text-base text-n-slate-11">
              Yapay zekâ ürünü inceliyor, birkaç saniye…
            </div>

            <div v-else class="flex flex-col gap-4">
              <div class="grid gap-3 md:grid-cols-3">
                <div class="p-4 rounded-xl bg-n-teal-9/10">
                  <p
                    class="flex items-center gap-1.5 mb-1 text-sm font-bold text-n-teal-11"
                  >
                    <span class="size-4 i-lucide-trending-up" /> En Büyük Fırsat
                  </p>
                  <p class="text-lg leading-snug text-n-slate-12">
                    {{ analysis.opportunity }}
                  </p>
                </div>
                <div class="p-4 rounded-xl bg-n-ruby-9/10">
                  <p
                    class="flex items-center gap-1.5 mb-1 text-sm font-bold text-n-ruby-11"
                  >
                    <span class="size-4 i-lucide-alert-triangle" /> En Büyük
                    Risk
                  </p>
                  <p class="text-lg leading-snug text-n-slate-12">
                    {{ analysis.risk }}
                  </p>
                </div>
                <div class="p-4 rounded-xl bg-n-brand/10">
                  <p
                    class="flex items-center gap-1.5 mb-1 text-sm font-bold text-n-brand"
                  >
                    <span class="size-4 i-lucide-check-circle" /> Bugün
                    Yapılacak
                  </p>
                  <p class="text-lg leading-snug text-n-slate-12">
                    {{ analysis.action }}
                  </p>
                </div>
              </div>

              <div class="flex items-center gap-2">
                <span class="text-base text-n-slate-11">Güven seviyesi:</span>
                <span
                  class="px-3 py-1 text-sm font-bold rounded-full"
                  :class="confidenceClass(analysis.confidence)"
                >
                  {{ analysis.confidence }}
                </span>
              </div>

              <p
                class="text-lg leading-relaxed whitespace-pre-line text-n-slate-12"
              >
                {{ analysis.narrative }}
              </p>
            </div>
          </section>

          <!-- ============ Sinyal kartları ============ -->
          <div class="grid gap-3 mb-6 sm:grid-cols-2 lg:grid-cols-3">
            <!-- Satış kaynağı -->
            <div
              class="p-4 rounded-2xl bg-n-solid-2 outline outline-1 outline-n-weak"
            >
              <p class="text-base text-n-slate-11">Satış (Ticimax + Shopify)</p>
              <p class="mt-1 text-3xl font-bold text-n-slate-12">
                {{ num(profile.satis.toplam_ticimax_shopify) }}
                <span class="text-base font-normal text-n-slate-11">adet</span>
              </p>
              <p class="mt-1 text-sm text-n-slate-11">
                direkt web {{ num(profile.satis.direkt_web) }} · mesaj-kaynaklı
                {{ num(profile.satis.mesaj_kaynakli_eslesen) }}
              </p>
              <p class="mt-1 text-xs text-n-slate-10">
                📅 {{ dateRange(profile.donemler.satis) }}
              </p>
            </div>

            <!-- İndirim bağımlılığı -->
            <div
              class="p-4 rounded-2xl bg-n-solid-2 outline outline-1 outline-n-weak"
            >
              <p class="text-base text-n-slate-11">İndirim Bağımlılığı</p>
              <p class="mt-1 text-3xl font-bold text-n-slate-12">
                %{{ num(profile.indirim_bagimliligi.indirimli_oran_yuzde) }}
              </p>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ num(profile.indirim_bagimliligi.indirimli_satis) }} /
                {{ num(profile.indirim_bagimliligi.toplam_satis) }} adet
                indirimli
              </p>
              <p class="mt-1 text-xs text-n-slate-10">📅 Tüm zamanlar</p>
            </div>

            <!-- Stok tükenme -->
            <div
              class="p-4 rounded-2xl bg-n-solid-2 outline outline-1 outline-n-weak"
            >
              <p class="text-base text-n-slate-11">Stok Tükenme Tahmini</p>
              <p class="mt-1 text-3xl font-bold text-n-slate-12">
                {{
                  profile.stok_tukenme.kalan_gun
                    ? `${num(profile.stok_tukenme.kalan_gun)} gün`
                    : '—'
                }}
              </p>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ num(profile.stok.total) }} adet stok · günlük
                {{ profile.stok_tukenme.gunluk_hiz }} satış
              </p>
              <p class="mt-1 text-xs text-n-slate-10">
                📅 Son 14 günün hızına göre
              </p>
            </div>

            <!-- Satış trendi -->
            <div
              class="p-4 rounded-2xl bg-n-solid-2 outline outline-1 outline-n-weak"
            >
              <p class="text-base text-n-slate-11">Satış Trendi (7 gün)</p>
              <p
                class="mt-1 text-3xl font-bold"
                :class="arrowClass(profile.trend.satis_yonu)"
              >
                {{ arrow(profile.trend.satis_yonu) }}
                {{ num(profile.trend.satis_son_7) }}
              </p>
              <p class="mt-1 text-sm text-n-slate-11">
                önceki 7 gün {{ num(profile.trend.satis_onceki_7) }} adet
              </p>
            </div>

            <!-- Harcama trendi -->
            <div
              class="p-4 rounded-2xl bg-n-solid-2 outline outline-1 outline-n-weak"
            >
              <p class="text-base text-n-slate-11">Harcama Trendi (7 gün)</p>
              <p
                class="mt-1 text-3xl font-bold"
                :class="arrowClass(profile.trend.harcama_yonu)"
              >
                {{ arrow(profile.trend.harcama_yonu) }}
                {{ formatCurrency(profile.trend.harcama_son_7) }}
              </p>
              <p class="mt-1 text-sm text-n-slate-11">
                önceki 7 gün
                {{ formatCurrency(profile.trend.harcama_onceki_7) }}
              </p>
            </div>

            <!-- Brüt kâr -->
            <div
              class="p-4 rounded-2xl bg-n-solid-2 outline outline-1 outline-n-weak"
            >
              <p class="text-base text-n-slate-11">Brüt Kâr</p>
              <p
                v-if="profile.brut_karlilik.maliyet_bilinmiyor"
                class="mt-1 text-xl font-bold text-n-slate-11"
              >
                Maliyet girilmemiş
              </p>
              <template v-else>
                <p class="mt-1 text-3xl font-bold text-n-slate-12">
                  {{ formatCurrency(profile.brut_karlilik.brut_kar_tl) }}
                </p>
                <p class="mt-1 text-sm text-n-slate-11">
                  marj %{{ num(profile.brut_karlilik.brut_marj_yuzde) }} · ciro
                  {{ formatCurrency(profile.brut_karlilik.ciro_tl) }}
                </p>
              </template>
            </div>
          </div>

          <!-- ============ Reklam özeti (kampanya amacına göre) ============ -->
          <section
            class="p-5 mb-6 rounded-2xl bg-n-solid-2 outline outline-1 outline-n-weak"
          >
            <div
              class="flex flex-wrap items-baseline justify-between gap-2 mb-1"
            >
              <h2 class="text-xl font-bold text-n-slate-12">Reklam Özeti</h2>
              <span class="text-base font-semibold text-n-slate-11">
                Toplam {{ formatCurrency(profile.reklam.toplam_harcama_tl) }}
              </span>
            </div>
            <p class="mb-3 text-sm text-n-slate-11">
              <span class="font-semibold">Hesap:</span>
              {{ (profile.reklam.hesaplar || []).join(', ') || '—' }}
              <span class="mx-1 text-n-slate-9">·</span>
              📅 {{ dateRange(profile.donemler.reklam) }}
            </p>
            <div class="grid gap-4 md:grid-cols-2">
              <!-- Mesaj kampanyaları -->
              <div class="p-4 rounded-xl bg-n-solid-1">
                <p
                  class="flex items-center gap-1.5 mb-2 text-base font-bold text-n-slate-12"
                >
                  <span class="size-5 i-lucide-message-circle text-n-brand" />
                  Mesaj Kampanyaları
                  <span class="text-sm font-normal text-n-slate-11"
                    >({{ profile.reklam.mesaj_kampanya.kampanya_sayisi }})</span
                  >
                </p>
                <div class="grid grid-cols-3 gap-2">
                  <div>
                    <p class="text-xs text-n-slate-11">Harcama</p>
                    <p class="text-lg font-bold text-n-slate-12">
                      {{
                        formatCurrency(profile.reklam.mesaj_kampanya.harcama_tl)
                      }}
                    </p>
                  </div>
                  <div>
                    <p class="text-xs text-n-slate-11">Mesaj</p>
                    <p class="text-lg font-bold text-n-slate-12">
                      {{ num(profile.reklam.mesaj_kampanya.mesaj_sayisi) }}
                    </p>
                  </div>
                  <div>
                    <p class="text-xs text-n-slate-11">Mesaj Başı</p>
                    <p class="text-lg font-bold text-n-slate-12">
                      {{
                        profile.reklam.mesaj_kampanya.mesaj_basina_maliyet_tl
                          ? formatCurrency(
                              profile.reklam.mesaj_kampanya
                                .mesaj_basina_maliyet_tl
                            )
                          : '—'
                      }}
                    </p>
                  </div>
                </div>
                <!-- Mesajlardan gelen GERÇEK satış (İnteraktif.tech + Yapaytech) -->
                <div
                  class="grid grid-cols-2 gap-2 p-2 mt-2 rounded-lg bg-n-brand/10"
                >
                  <div>
                    <p class="text-xs text-n-brand">Mesajdan Gelen Satış</p>
                    <p class="text-lg font-bold text-n-slate-12">
                      {{
                        num(profile.reklam.mesaj_kampanya.mesaj_kaynakli_satis)
                      }}
                      <span class="text-xs font-normal text-n-slate-11">adet</span>
                    </p>
                  </div>
                  <div>
                    <p class="text-xs text-n-brand">Satış Başına Maliyet</p>
                    <p class="text-lg font-bold text-n-slate-12">
                      {{
                        profile.reklam.mesaj_kampanya.satis_basina_maliyet_tl
                          ? formatCurrency(
                              profile.reklam.mesaj_kampanya
                                .satis_basina_maliyet_tl
                            )
                          : '—'
                      }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Satış kampanyaları -->
              <div class="p-4 rounded-xl bg-n-solid-1">
                <p
                  class="flex items-center gap-1.5 mb-2 text-base font-bold text-n-slate-12"
                >
                  <span class="size-5 i-lucide-shopping-bag text-n-teal-11" />
                  Satış Kampanyaları
                  <span class="text-sm font-normal text-n-slate-11"
                    >({{ profile.reklam.satis_kampanya.kampanya_sayisi }})</span
                  >
                </p>
                <div class="grid grid-cols-3 gap-2">
                  <div>
                    <p class="text-xs text-n-slate-11">Harcama</p>
                    <p class="text-lg font-bold text-n-slate-12">
                      {{
                        formatCurrency(profile.reklam.satis_kampanya.harcama_tl)
                      }}
                    </p>
                  </div>
                  <div>
                    <p class="text-xs text-n-slate-11">Meta Satış</p>
                    <p class="text-lg font-bold text-n-slate-12">
                      {{ num(profile.reklam.satis_kampanya.meta_satin_alma) }}
                    </p>
                  </div>
                  <div>
                    <p class="text-xs text-n-slate-11">Satış Başı</p>
                    <p class="text-lg font-bold text-n-slate-12">
                      {{
                        profile.reklam.satis_kampanya
                          .satin_alma_basina_maliyet_tl
                          ? formatCurrency(
                              profile.reklam.satis_kampanya
                                .satin_alma_basina_maliyet_tl
                            )
                          : '—'
                      }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <p
              class="flex items-start gap-2 p-3 mt-4 text-sm rounded-lg bg-n-amber-9/10 text-n-amber-11"
            >
              <span class="flex-shrink-0 mt-0.5 size-4 i-lucide-info" />
              {{ profile.satis_notu }}
            </p>
          </section>

          <!-- ============ Üst üste binen kampanyalar (yamyamlık) ============ -->
          <section
            v-if="(profile.kampanyalar || []).length"
            class="p-5 mb-6 rounded-2xl bg-n-solid-2 outline outline-1 outline-n-weak"
          >
            <h2 class="mb-1 text-xl font-bold text-n-slate-12">
              Bu Ürüne Bağlı Kampanyalar
              <span class="text-base font-normal text-n-slate-11">
                ({{ (profile.kampanyalar || []).length }} kampanya ·
                {{ campaignsByAccount.length }} hesap)
              </span>
            </h2>
            <p
              v-if="profile.reklam.aktif_kampanya > 1"
              class="mb-3 text-sm text-n-amber-11"
            >
              ⚠ {{ profile.reklam.aktif_kampanya }} kampanya aynı anda aktif —
              birbirini yiyor olabilir (yamyamlık).
            </p>

            <!-- Hesaba göre gruplu -->
            <div
              v-for="[hesap, kampanyalar] in campaignsByAccount"
              :key="hesap"
              class="mb-4 last:mb-0"
            >
              <p
                class="flex items-center gap-2 mb-2 text-base font-bold text-n-slate-12"
              >
                <span class="size-4 i-lucide-building-2 text-n-slate-10" />
                {{ hesap }}
                <span class="text-sm font-normal text-n-slate-11"
                  >({{ kampanyalar.length }})</span
                >
              </p>
              <div class="flex flex-col gap-2">
                <div
                  v-for="(c, i) in kampanyalar"
                  :key="i"
                  class="flex items-center justify-between gap-3 p-3 rounded-xl bg-n-solid-1"
                >
                  <div class="min-w-0">
                    <div class="flex items-center gap-2">
                      <span
                        class="px-2 py-0.5 text-xs font-bold rounded-full"
                        :class="
                          c.amac === 'mesaj'
                            ? 'bg-n-brand/15 text-n-brand'
                            : 'bg-n-teal-9/15 text-n-teal-11'
                        "
                      >
                        {{ c.amac }}
                      </span>
                      <p class="text-base font-medium truncate text-n-slate-12">
                        {{ c.ad }}
                      </p>
                    </div>
                    <p class="mt-0.5 text-sm text-n-slate-11">
                      {{ c.durum }} · {{ fmtDate(c.baslangic) }}
                    </p>
                  </div>
                  <span
                    class="flex-shrink-0 text-base font-bold text-n-slate-12"
                  >
                    {{ formatCurrency(c.harcama_tl) }}
                  </span>
                </div>
              </div>
            </div>
          </section>
        </template>
      </div>
    </div>
  </div>
</template>
